<template>
  <CCardBody>
    <!-- Filters -->
    <CCard class="filters" accent-color="warning">
      <CCardHeader role="button" class="text-warning shadow-none card-header" @click="resetFilters">
        <strong class="m-0">
          <CIcon :name="`cil-filter${$store.state.filterShow ? '-x' : ''}`" /> Filters
        </strong>
        <div class="card-header-actions">
          <CIcon :name="`cil-chevron-${$store.state.filterShow ? 'bottom' : 'top'}`" />
        </div>
      </CCardHeader>

      <CCollapse :show="$store.state.filterShow">
        <CCardBody class="p-2">
          <div class="form-container">
            <div class="filter-item">
              <v-select
                class="v-select-filter"
                placeholder="Select restaurant.."
                v-model="selectedRestaurant"
                :options="restaurants"
                @input="inputFilter"
              />
            </div>

            <div class="filter-item">
              <v-select
                class="v-select-filter"
                :placeholder="users.length == 0 ? 'Search for a user..' : 'Select or search for a user..'"
                v-model="selectedUser"
                :options="users"
                :searchable="true"
                :loading="searching && users.length == 0"
                @search="searchUsers"
                @input="inputFilter"
              >
                <template v-slot:option="user">
                  <div class="d-flex align-items-center">
                    <div class="c-avatar mr-2">
                      <img :src="user.image_icon || user.gravatar" class="c-avatar-img">
                    </div>
                    <span class="small"><strong>{{ user.first_name }}</strong> #{{ user.id }} ({{ user.email }})</span>
                  </div>
                </template>
                <template slot="selected-option" slot-scope="user">
                  {{ user.first_name }} #{{ user.id }}
                </template>
                <span slot="no-options">Type to search users..</span>
              </v-select>
            </div>

            <div class="filter-item">
              <v-select
                class="v-select-filter"
                v-model="createdViaPoints"
                :options="booleanFilterOptions"
                :reduce="option => option.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>

            <div class="filter-item">
              <v-select
                class="v-select-filter"
                v-model="isAppOnly"
                :options="appOnlyFilterOptions"
                :reduce="option => option.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>

            <div class="filter-item">
              <v-select
                class="v-select-filter"
                v-model="isFirstTimeOnly"
                :options="firstOrdersFilterOptions"
                :reduce="option => option.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>

            <div class="filter-item">
              <v-select
                class="v-select-filter"
                v-model="onlyDelivery"
                :options="onlyDeliveryFilterOptions"
                :reduce="option => option.value"
                :searchable="false"
                @input="inputFilter"
              />
            </div>

            <div class="filter-item expires-at-filter">
              <div class="input-group">
                <v-select
                  class="v-select-filter expires-at-operator"
                  placeholder="Expires at"
                  v-model="expiresAtOperator"
                  :options="expiresAtOperatorOptions"
                  :reduce="option => option.value"
                  :searchable="false"
                  @input="inputFilter"
                />
                <input
                  type="date"
                  class="form-control expires-at-date"
                  v-model="expiresAtDate"
                  @input="inputFilter"
                >
              </div>
            </div>

            <div class="filter-item search-filter">
              <CInput
                class="mb-0 search"
                type="search"
                placeholder="Search for something else ..."
                v-model="search"
                @input="searchFilter"
              />
            </div>
          </div>
        </CCardBody>
      </CCollapse>
    </CCard>

    <div class="table-responsive">
      <CDataTable
        striped
        hover
        :items="loadedItems"
        :fields="fields"
        :sorter="{ external: true, resetable: true }"
        :column-filter="{ external: true, lazy: true }"
        :itemsPerPageSelect="{
          external: true,
          values: [5, 15, 25, 50, 100, 250, 500],
        }"
        :items-per-page.sync="itemsPerPage"
        :loading="loading"
        :noItemsView="{
          noResults: 'No filtering results are available!',
          noItems: 'No items found!',
        }"
        @update:sorter-value="sorterValue"
        @pagination-change="paginationChange"
      >
        <template #actions="{ item }">
          <td>
            <div class="d-flex align-items-center justify-content-center">
              <CButton
                :to="{ name: 'Edit Voucher', params: { id: item.id } }"
                v-c-tooltip="{ content: 'Edit', placement: 'top' }"
                size="sm"
                color="info"
                variant="ghost"
                shape="pill"
              >
                <CIcon name="cil-align-left" />
              </CButton>

              <CButton
                :to="{ name: 'Add New Voucher', query: { copy_from: item.id } }"
                v-c-tooltip="{ content: 'Copy', placement: 'top' }"
                size="sm"
                color="primary"
                variant="ghost"
                shape="pill"
                class="ml-1"
              >
                <CIcon name="cil-copy" />
              </CButton>

              <CButton
                v-c-tooltip="{ content: 'Recalculate usage', placement: 'top' }"
                size="sm"
                color="warning"
                variant="ghost"
                shape="pill"
                class="ml-1"
                :disabled="usageStatsLoadingId === item.id"
                @click="refreshUsageStats(item)"
              >
                <CIcon name="cil-sync" />
              </CButton>
            </div>
          </td>
        </template>

        <template #id="{ item }">
          <td>
            {{ item.id }}
          </td>
        </template>

        <template #code="{ item }">
          <td>
            <strong>{{ item.code }}</strong>
          </td>
        </template>

        <template #name="{ item }">
          <td>{{ item.name }}</td>
        </template>

        <template #discount_amount="{ item }">
          <td class="text-center">
            <strong v-if="item.is_fixed">{{ item.discount_amount | toCurrency('remove_thousand_separator') }}</strong>
            <strong v-else>% {{ item.discount_amount }}</strong>
          </td>
        </template>

        <template #minvalue="{ item }">
          <td class="text-center">
            <strong>{{ item.minvalue | toCurrency('remove_thousand_separator') }}</strong>
          </td>
        </template>

        <template #uses="{ item }">
          <td class="text-center">
            <strong>{{ item.uses }}</strong> / {{ item.max_uses }}
          </td>
        </template>

        <template #usage_count="{ item }">
          <td class="text-center">
            <strong>{{ formatUsageCount(item.usage_count) }}</strong>
          </td>
        </template>

        <template #unique_user_usage_count="{ item }">
          <td class="text-center">
            <strong>{{ formatUsageCount(item.unique_user_usage_count) }}</strong>
          </td>
        </template>

        <template #max_uses_user="{ item }">
          <td class="text-center">
            <strong>{{ item.max_uses_user }}</strong>
          </td>
        </template>

        <template #is_app_only="{ item }">
          <td class="text-center">
            <strong class="text-success">{{ item.is_app_only ? 'YES' : '' }}</strong>
          </td>
        </template>

        <template #only_delivery="{ item }">
          <td class="text-center">
            <strong class="text-success">{{ item.only_delivery ? 'YES' : '' }}</strong>
          </td>
        </template>

        <template #is_first_time_only="{ item }">
          <td class="text-center">
            <strong class="text-success">{{ item.is_first_time_only ? 'YES' : '' }}</strong>
          </td>
        </template>

        <template #is_fixed="{ item }">
          <td class="text-center">
            <strong class="text-success">{{ item.is_fixed ? 'YES' : '' }}</strong>
          </td>
        </template>

        <template #user_id="{ item }">
          <td>
            <CLink v-if="item.user" :to="{ name: 'User', params: { id: item.user_id } }" v-c-tooltip="{ content: `# ${item.user_id}` }">
              {{ item.user.first_name }}
            </CLink>
          </td>
        </template>

        <template #untill_user_id="{ item }">
          <td class="text-center">
            <strong>{{ item.untill_user_id }}</strong>
          </td>
        </template>

        <template #resto_id="{ item }">
          <td>
            <CLink v-if="item.restaurant" :to="{ name: 'View Restaurant', params: { id: item.resto_id } }" v-c-tooltip="{ content: `# ${item.resto_id}` }">
              {{ item.restaurant.restaurant_name }}
            </CLink>
          </td>
        </template>

        <template #starts_at="{ item }">
          <td>
            <span v-if="item.starts_at">{{ moment(item.starts_at).format('LL') }}</span>
          </td>
        </template>

        <template #expires_at="{ item }">
          <td>
            <span v-if="item.expires_at">{{ moment(item.expires_at).format('LL') }}</span>
          </td>
        </template>
      </CDataTable>
    </div>

    <CCard class="actions sticky-bottom">
      <CCardBody class="p-2">
        <div class="d-flex flex-wrap align-items-center" style="gap: 0.75rem">
          <div>
            <h5 class="mt-1">Total: <span class="d-inline count bg-primary pb-1">{{ total }}</span></h5>
          </div>

          <div v-if="pages > 1" class="ml-auto">
            <CPagination align="center" :dots="false" :pages="pages" :active-page.sync="activePage" />
          </div>
        </div>
      </CCardBody>
    </CCard>

    <mc-spinner :opacity="0.8" v-show="loading" :mtop="(32 + mcSpinnerMarginTop) + 'px'" />
  </CCardBody>
</template>

<script>
import { mapState } from 'vuex'
import _debounce from 'lodash/debounce';

export default {
  name: "BackendTable",
  data() {
    return {
      loadedItems: [],
      fields: [
        { key: "actions", label: "", filter: false, sorter: false, _style: "min-width: 122px; width: 122px;" },
        { key: "id", label: "ID", filter: false, _style: "min-width: 64px; width: 64px;" },
        { key: "code", label: "Code", filter: false, sorter: false, _style: "min-width: 96px; width: 200px;" },
        { key: "name", label: "Name", filter: false, _style: "min-width: 150px;" },
        { key: "discount_amount", filter: false, _style: "min-width: 96px; width: 120px; text-align: center;" },
        { key: "minvalue", label: "Min. Value", filter: false, _style: "min-width: 96px; width: 120px; text-align: center;" },
        { key: "uses", label: "Uses / Max", filter: false, _style: "min-width: 100px; width: 120px; text-align: center;" },
        { key: "usage_count", label: "Real Uses", filter: false, sorter: false, _style: "min-width: 96px; width: 110px; text-align: center;" },
        { key: "unique_user_usage_count", label: "Unique Users", filter: false, sorter: false, _style: "min-width: 112px; width: 124px; text-align: center;" },
        { key: "max_uses_user", label: "Max. Usage Per User", filter: false, _style: "min-width: 110px; width: 120px; text-align: center;" },
        { key: "only_delivery", label: "Only Delivery", filter: false, _style: "min-width: 100px; width: 110px; text-align: center;" },
        { key: "is_app_only", label: "App Only", filter: false, _style: "min-width: 90px; width: 96px; text-align: center;" },
        { key: "is_first_time_only", label: "First Orders", filter: false, _style: "min-width: 110px; width: 120px; text-align: center;" },
        { key: "user_id", label: "User", filter: false, _style: "min-width: 150px;" },
        { key: "untill_user_id", label: "Valid Until The User", filter: false, _style: "min-width: 100px; width: 120px; text-align: center;" },
        { key: "resto_id", label: "Restaurant", filter: false, _style: "min-width: 150px;" },
        { key: "starts_at", label: "Start", filter: false, _style: "min-width: 140px; width: 140px;" },
        { key: "expires_at", label: "Expire", filter: false, _style: "min-width: 140px; width: 140px;" },
      ],

      activePage: 1,
      pages: 1,
      total: 0,
      items_per_page: null,
      orderBy: null,
      direction: null,

      loading: false,
      searching: false,
      usageStatsLoadingId: null,

      mcSpinnerMarginTop: 0,

      search: null,
      restaurants: [],
      users: [],
      booleanFilterOptions: [
        { value: 'all', label: 'Created via points: All' },
        { value: '1', label: 'Created via points: Yes' },
        { value: '0', label: 'Created via points: No' },
      ],
      appOnlyFilterOptions: [
        { value: 'all', label: 'App only: All' },
        { value: '1', label: 'App only: Yes' },
        { value: '0', label: 'App only: No' },
      ],
      firstOrdersFilterOptions: [
        { value: 'all', label: 'First orders: All' },
        { value: '1', label: 'First orders: Yes' },
        { value: '0', label: 'First orders: No' },
      ],
      onlyDeliveryFilterOptions: [
        { value: 'all', label: 'Only delivery: All' },
        { value: '1', label: 'Only delivery: Yes' },
        { value: '0', label: 'Only delivery: No' },
      ],
      expiresAtOperatorOptions: [
        { value: 'gt', label: 'Expires at >' },
        { value: 'gte', label: 'Expires at >=' },
        { value: 'eq', label: 'Expires at =' },
        { value: 'lte', label: 'Expires at <=' },
        { value: 'lt', label: 'Expires at <' },
      ],
    };
  },

  beforeMount() {
    this.searchFilter = _debounce(async () => {
      await this.getAllItems();
    }, 1000);

    this.searchUsers = _debounce(async (val) => {
      await this.getUsers(val);
    }, 500);
  },

  async mounted() {
    this.activePage = this.queryPage;
    this.applyUserFilterFromRoute();

    await Promise.all([
      this.getRestaurants(),
      this.getAllItems()
    ]);

    window.onscroll = () => {
      this.mcSpinnerMarginTop = document.documentElement.scrollTop;
    }
  },

  watch: {
    reloadParams() {
      if (this.queryPage != this.activePage) {
        this.$router.push({ name: 'Vouchers', query: { page: this.activePage } });
      }

      this.onTableChange();
    },
  },

  computed: {
    ...mapState(['filters']),

    itemsPerPage: {
      get: function () { return this.items_per_page ? this.items_per_page : parseInt(process.env.VUE_APP_ITEMS_PER_PAGE) },
      set: function (newValue) { this.items_per_page = newValue }
    },
    reloadParams() { return [this.activePage]; },
    queryPage() { return parseInt(this.$route.query.page) || 1; },

    selectedRestaurant: {
      get() { return this.filters.vouchers.selectedRestaurant },
      set(value) { this.filters.vouchers.selectedRestaurant = value },
    },
    selectedUser: {
      get() { return this.filters.vouchers.selectedUser },
      set(value) { this.filters.vouchers.selectedUser = value },
    },
    createdViaPoints: {
      get() { return this.filters.vouchers.createdViaPoints },
      set(value) { this.filters.vouchers.createdViaPoints = value },
    },
    isAppOnly: {
      get() { return this.filters.vouchers.isAppOnly },
      set(value) { this.filters.vouchers.isAppOnly = value },
    },
    isFirstTimeOnly: {
      get() { return this.filters.vouchers.isFirstTimeOnly },
      set(value) { this.filters.vouchers.isFirstTimeOnly = value },
    },
    onlyDelivery: {
      get() { return this.filters.vouchers.onlyDelivery },
      set(value) { this.filters.vouchers.onlyDelivery = value },
    },
    expiresAtOperator: {
      get() { return this.filters.vouchers.expiresAtOperator },
      set(value) { this.filters.vouchers.expiresAtOperator = value },
    },
    expiresAtDate: {
      get() { return this.filters.vouchers.expiresAtDate },
      set(value) { this.filters.vouchers.expiresAtDate = value },
    },
  },

  methods: {
    applyUserFilterFromRoute() {
      const userId = Number(this.$route.query.user_id);

      if (!Number.isInteger(userId) || userId <= 0) {
        return;
      }

      const userName = String(this.$route.query.user_name || 'User');
      const selectedUser = {
        id: userId,
        first_name: userName,
        label: `i#${userId} ${userName}`,
      };

      this.selectedRestaurant = null;
      this.selectedUser = selectedUser;
      this.createdViaPoints = 'all';
      this.isAppOnly = 'all';
      this.isFirstTimeOnly = 'all';
      this.onlyDelivery = 'all';
      this.expiresAtOperator = null;
      this.expiresAtDate = null;
      this.search = '';

      if (!this.users.some((user) => user.id === userId)) {
        this.users.unshift(selectedUser);
      }
    },

    normalizeBooleanFilter(value) {
      return value === 'all' ? null : value;
    },

    async getAllItems() {
      this.loading = true;

      const url = new URL(this.$backend.VOUCHERS.GET_ALL);
      const params = {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        'order-by': this.orderBy,
        direction: this.direction,
        'only-availables': false,
        'include-usage-stats': 1,
        'restaurant-id': this.selectedRestaurant?.value,
        'user-id': this.selectedUser?.id,
        created_via_points: this.normalizeBooleanFilter(this.createdViaPoints),
        is_app_only: this.normalizeBooleanFilter(this.isAppOnly),
        is_first_time_only: this.normalizeBooleanFilter(this.isFirstTimeOnly),
        only_delivery: this.normalizeBooleanFilter(this.onlyDelivery),
        expires_at_operator: this.expiresAtOperator,
        expires_at_date: this.expiresAtDate,
        search: this.search,
      };

      Object.keys(params).forEach((key) => {
        if (
          typeof params[key] !== "undefined" &&
          params[key] !== null &&
          params[key] !== "" &&
          params[key].length !== 0
        ) {
          url.searchParams.append(key, params[key]);
        }
      });

      this.$axios.get(url)
        .then((response) => response.data)
        .then((items) => {
          if (items.current_page > items.last_page) {
            this.activePage = 1;
          } else {
            this.activePage = items.meta.current_page;
            this.pages = items.meta.last_page;
            this.total = items.meta.total;
            this.loadedItems = items.data;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    async getRestaurants() {
      const storedRestaurants = sessionStorage.getItem('restaurants');

      if (storedRestaurants) {
        this.restaurants = JSON.parse(storedRestaurants);
      } else {
        try {
          const response = await this.$axios.get(this.$backend.RESTAURANT.GET_ALL_NAME);
          this.restaurants = response.data;
          sessionStorage.setItem('restaurants', JSON.stringify(this.restaurants));
        } catch (error) {
          this.restaurants = [];
        }
      }
    },
    async getUsers(val) {
      if (val.length < 3) {
        return;
      }

      this.searching = true;
      try {
        const response = await this.$axios.get(this.$backend.USERS.GET_ALL, {
          params: {
            search: val,
            'get-relations': false,
            'except-couriers': false,
          }
        });
        this.users = response.data.data.map((el) => {
          return { ...el, label: `i#${el.id} ${el.first_name} ${el.email}` };
        });
      } catch (error) {
        this.users = [];
      } finally {
        this.searching = false;
      }
    },

    onTableChange() {
      this.getAllItems();
    },
    paginationChange(value) {
      this.itemsPerPage = value;
      this.getAllItems();
    },
    sorterValue(item) {
      this.orderBy = item.column;
      this.direction = item.column ? (item.asc ? "asc" : "desc") : null;
      this.getAllItems();
    },

    async inputFilter() {
      await this.getAllItems();
    },
    formatUsageCount(value) {
      return Number(value || 0).toLocaleString();
    },
    async refreshUsageStats(item) {
      this.usageStatsLoadingId = item.id;

      try {
        const url = this.$backend.VOUCHERS.USAGE_STATS.replace('{id}', item.id);
        const { data } = await this.$axios.get(url);

        this.$set(item, 'usage_count', data.usage_count);
        this.$set(item, 'unique_user_usage_count', data.unique_user_usage_count);
      } catch (error) {
        this.$toast.error('Failed to recalculate voucher usage.');
      } finally {
        this.usageStatsLoadingId = null;
      }
    },
    resetFilters() {
      if (this.$store.state.filterShow) {
        this.selectedRestaurant = null;
        this.selectedUser = null;
        this.createdViaPoints = 'all';
        this.isAppOnly = 'all';
        this.isFirstTimeOnly = 'all';
        this.onlyDelivery = 'all';
        this.expiresAtOperator = null;
        this.expiresAtDate = null;
        this.search = "";
        this.getAllItems();
      }
      this.$store.commit('toggle', 'filterShow');
    },
  },
};
</script>

<style>
.table th,
.table td {
  padding: 0.5rem 0.25rem;
  vertical-align: middle;
}

.search input {
  font-size: 1rem !important;
}
</style>

<style scoped>
.filters .form-container {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 1rem;
}

.expires-at-filter .input-group {
  flex-wrap: nowrap;
}

.expires-at-operator {
  min-width: 140px;
}

.expires-at-date {
  min-width: 144px;
}
</style>
